import React, { useEffect, useState } from 'react';
import { Survey } from 'survey-react-ui';
import { Model } from 'survey-core';

import FadeIn from 'legacy/common/components/transition/FadeIn';
import {
  useGetRequestToPublishForm,
  useSubmitRequestToPublishForm
} from '../../hooks';
import Redirect from '../../../components/util/Redirect/Redirect';
import Loader from '../../../common/components/layout/Loader';
import { Theme } from 'theme/survey-js/theme';
import Header from 'legacy/common/components/wysiwyg-form/Header';
import { useEventEditorContext } from '../../contexts/EventEditorContext';
import { useGetAssociationWorkflow } from 'api/queries/workflows/workflows';
import { useSurveyFileHandlers } from 'hooks/useSurveyFileHandlers';

import 'survey-core/survey-core.min.css';
import { useGetAssociationFormById } from 'api/queries/workflows/forms';

const SurveyWrapper = ({
  schema,
  onSubmit,
  onUploadFiles,
  onClearFiles
}: {
  schema: Record<string, any>;
  onSubmit: (data: Record<string, any>) => void;
  onUploadFiles: (sender: any, options: any) => void;
  onClearFiles: (sender: any, options: any) => void;
}) => {
  const survey = new Model(schema);
  survey.applyTheme(Theme);
  survey.onComplete.add(onSubmit as any);
  survey.onUploadFiles.add(onUploadFiles);
  survey.onClearFiles.add(onClearFiles);
  return <Survey model={survey} />;
};

const RequestToPublishForm: React.FC = () => {
  const {
    associationId,
    requestToPublishFormId,
    requestToPublishWorkflowId,
    requestPublication,
    requestPublicationWorkflow,
    isDraft,
    isEditing,
    isLoading: headerIsLoading,
    lastUpdated,
    handleBack,
    navigateToEventSettings
  } = useEventEditorContext();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [surveyData, setSurveyData] = useState<Record<string, any>>({});
  const { data, isLoading, isError } = useGetRequestToPublishForm(
    requestToPublishFormId
  );

  const submitForm = useSubmitRequestToPublishForm();
  const {
    data: workflowData,
    isLoading: isLoadingWorkflow,
    isError: isErrorWorkflow
  } = useGetAssociationWorkflow({
    associationId,
    workflowId: requestToPublishWorkflowId
  });

  const {
    data: formData,
    isLoading: isLoadingForm,
    isError: isErrorForm
  } = useGetAssociationFormById({
    associationId,
    formId: workflowData?.formId
  });

  const {
    onUploadFiles,
    onClearFiles,
    executePendingDeletions,
    clearPendingDeletions
  } = useSurveyFileHandlers({
    associationId
  });

  // Cleanup pending deletions on unmount
  React.useEffect(() => () => clearPendingDeletions(), [clearPendingDeletions]);

  const onSubmit = ({ data }) => {
    setIsSubmitting(true);
    if (requestToPublishWorkflowId) {
      requestPublicationWorkflow(data);
    } else {
      submitForm.mutate(
        {
          formId: requestToPublishFormId,
          body: data
        },
        {
          onSuccess: async (res) => {
            // Execute pending file deletions after successful form submission
            await executePendingDeletions();
            requestPublication({ responseId: res.id });
          },
          onError: () => {
            // Clear pending deletions if form submission fails
            clearPendingDeletions();
            setIsSubmitting(false);
          }
        }
      );
    }
  };

  useEffect(() => {
    if (formData?.schema) {
      setSurveyData(formData.schema);
    } else if (data?.schema && !formData?.schema) {
      setSurveyData(data.schema);
    }
  }, [data?.schema, formData?.schema]);

  if (isLoading || isSubmitting || isLoadingWorkflow || isLoadingForm) {
    return <Loader isLoading />;
  }

  if (
    isError ||
    (requestToPublishWorkflowId && isErrorWorkflow) ||
    (isErrorForm && requestToPublishFormId) ||
    (requestToPublishFormId && !data?.id)
  ) {
    return <Redirect to="/404" />;
  }

  return (
    <>
      <Header
        isDraft={isDraft}
        isEditing={isEditing}
        title="Request to publish"
        isLoading={headerIsLoading}
        lastUpdated={lastUpdated}
        onBack={handleBack}
        onMenuClick={navigateToEventSettings}
      />
      <FadeIn>
        <div className="pt-[50px] md:pt-[120px] -mt-[90px] pb-20">
          <SurveyWrapper
            schema={surveyData}
            onSubmit={onSubmit}
            onUploadFiles={onUploadFiles}
            onClearFiles={onClearFiles}
          />
        </div>
      </FadeIn>
    </>
  );
};

export default RequestToPublishForm;
